import cn from 'classnames';
import { INFRA_GEO_LOCATION_FLOW } from 'core-lib/constants/location';
import GoogleMapScript from 'core-lib/components/GoogleMapScript';
import SelectLocation from 'core-lib/components/SelectLocation';
import StatusBar from 'core-lib/components/StatusBar';
import { defaultCenterCoords } from 'core-lib/data/common';
import { useProfile } from 'core-lib/hooks/useProfile';
import useReverseGeoCode from 'core-lib/services/useReverseGeoCode';
import { Address, AddressKeys, AddressStatusType } from 'core-lib/types/Address';
import {
  UserGeoAddressType,
  UserLocationRequestType,
  UserLocationRequirementType,
  UserLocationType,
} from 'core-lib/types/Location';
import { CoordsType } from 'core-lib/types/Map';
import { trackCustomGTMEvent } from 'core-lib/utils/events';
import { EventCategoryName } from 'core-lib/types/Events';
import { getGeoLocation, LOCATION_STATUS_TYPES } from 'core-lib/utils/location';
import { isLocationV2Available } from 'core-lib/utils/locationV2';
import { getDeviceId } from 'core-lib/utils/source';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppBar } from 'ui-v2/components/AppBar';
import { Button } from 'ui-v2/components/Button';
import Modal from 'ui-v2/components/Modal';
import { ModalTypeTypes } from 'ui-v2/components/Modal/Modal';
import { Typography } from 'ui-v2/components/Typography';
import { checkServiceability } from '../../services/checkServiceability';
import { UserAddress } from '../../types/types';
import { removeDuplicateValues } from '../../utils/utils';
import AddAddress from '../AddAddress';
import Unserviceable from '../Unserviceable';
import Map from './Map';
import s from './MapAddress.module.scss';
import Shimmer from './Shimmer';
import Icon from 'ui-v2/components/Icon/Icon';

type LocType = {
  activeLocType: 'device' | 'search';
  deviceLoc?: {
    status: LOCATION_STATUS_TYPES;
    coords: CoordsType;
  };
  searchLoc?: {
    coords: CoordsType;
    placeId: number | string;
    formattedAddress: string;
  };
};

const INITIAL_LOC: LocType = {
  activeLocType: 'device',
  deviceLoc: {
    status: LOCATION_STATUS_TYPES.Pending,
    coords: {
      lat: defaultCenterCoords.lat,
      lng: defaultCenterCoords.lng,
    },
  },
};

const renderText = (isEditAddress, address, locationAddress) => {
  let mainText, secondaryText;
  mainText = address?.name || removeDuplicateValues(address);

  if (isEditAddress) {
    secondaryText =
      address?.vicinity ||
      address?.addressLine2 ||
      `${address?.city || ''} ${address?.state || ''} ${address?.pincode || ''}`;
  }
  // else {
  switch (locationAddress.status) {
    case AddressStatusType.Denied:
      mainText = 'Location not available';
      secondaryText = 'Please provide location access or search for a location';
      break;
    case AddressStatusType.Failed:
      mainText = 'Please try again';
      break;
    case AddressStatusType.Success:
      secondaryText =
        address?.vicinity ||
        address?.addressLine2 ||
        `${address?.city || ''} ${address?.state || ''} ${address?.pincode || ''}`;
      break;
    default:
      break;
  }
  // }

  return (
    <>
      <Typography
        label={mainText || `Please wait`}
        variant="label_1_semi_bold"
        className={s.truncate}
      />
      {secondaryText && (
        <Typography
          label={secondaryText}
          variant="body_3_regular"
          color={locationAddress.status === AddressStatusType.Denied ? 'alert' : 'primary'}
          className={s.truncate}
        />
      )}
    </>
  );
};

type MapAddressProps = {
  onClose: () => void;
  onDone: () => void;
  showDetailAddress?: boolean;
  addressValidation: AddressKeys[];
  checkServiceable: boolean;
  serviceName: string;
  editAddress: UserAddress;
  isEditAddress: boolean;
  variant: ModalTypeTypes;
  zIndex: number;
};

export function MapAddress({
  onClose,
  onDone,
  addressValidation,
  checkServiceable,
  serviceName,
  editAddress,
  isEditAddress,
  variant,
  zIndex,
}: MapAddressProps) {
  const [isServiceable, setIsServiceable] = useState(true);
  const [showUnserviceable, setShowUnserviceable] = useState(false);

  const locObj = {
    requestType: UserLocationRequestType.RequestLatLngAndGeoAddress,
    payload: {
      locationType: UserLocationType.Current,
      geoAddressType: UserGeoAddressType.Current,
      locationRequirementType: UserLocationRequirementType.Mandatory,
    },
  };

  const { fetch: fetchLocation, address: locationAddress } = useReverseGeoCode({
    enableRGCApi: true,
    enableFallbackRGCApi: true,
    addressValidation: addressValidation,
    locationV2RequestPayload: locObj,
    geoLocationFlow: INFRA_GEO_LOCATION_FLOW,
  });

  // state
  const [addressPopup, setAddressPopup] = useState(false);
  const [location, setLocation] = React.useState<LocType>(INITIAL_LOC);
  const [address, setAddress] = useState<Address | UserAddress>();
  const [isAddressFromSearch, setIsAddressFromSearch] = useState(false);

  const [showAutoComplete, setShowAutoComplete] = React.useState<boolean>(false);
  const isFetchingDeviceLocationRef = useRef(false);
  const isAddressFromSearchRef = useRef(false);

  // selectors
  const mapStatus = useSelector(state => state.layout.mapStatus?.status) || 'pending';

  const syncMapToCoords = useCallback((coords: CoordsType, activeLocType: 'device' | 'search') => {
    setLocation(prev => ({
      ...prev,
      activeLocType,
      ...(activeLocType === 'device'
        ? {
            deviceLoc: {
              status: LOCATION_STATUS_TYPES.Success,
              coords,
            },
          }
        : {
            searchLoc: {
              coords,
              placeId: prev.searchLoc?.placeId ?? 1,
              formattedAddress: prev.searchLoc?.formattedAddress ?? '',
            },
          }),
    }));
  }, []);

  const goToCurrentLocation = useCallback(async () => {
    setIsAddressFromSearch(false);
    isAddressFromSearchRef.current = false;
    isFetchingDeviceLocationRef.current = true;

    // WebView uses native / location-v2 flow — keep the original fetchLocation path.
    if (isLocationV2Available()) {
      fetchLocation();
      return;
    }

    try {
      const coords = await getGeoLocation();
      syncMapToCoords(coords, 'device');
      fetchLocation(coords);
    } catch (err) {
      isFetchingDeviceLocationRef.current = false;
      setLocation(prev => ({
        ...prev,
        activeLocType: 'device',
        deviceLoc: {
          status:
            err === LOCATION_STATUS_TYPES.Denied
              ? LOCATION_STATUS_TYPES.Denied
              : LOCATION_STATUS_TYPES.Pending,
          coords: prev.deviceLoc?.coords ?? INITIAL_LOC.deviceLoc.coords,
        },
      }));
      fetchLocation();
    }
  }, [fetchLocation, syncMapToCoords]);

  const onDragEnd = useCallback(
    (map: any) => {
      const givenLocation = { lat: map.lat, lng: map.lng };
      setIsAddressFromSearch(false);
      isAddressFromSearchRef.current = false;
      isFetchingDeviceLocationRef.current = false;
      fetchLocation(givenLocation);
    },
    [fetchLocation],
  );

  const onSelectAddress = async selectedAddress => {
    if (checkServiceable) {
      const { isServiceable: locationServiceable } = await checkServiceability({
        currentAddress: selectedAddress,
        serviceName,
      });
      if (!locationServiceable) {
        setShowUnserviceable(true);
        setIsServiceable(false);
      } else {
        setIsServiceable(true);
      }
    }
  };

  useEffect(() => {
    if (editAddress) {
      setAddress({
        ...address,
        ...editAddress,
      });
      const givenLocation = {
        lat: +editAddress?.latitude,
        lng: +editAddress?.longitude,
      };
      fetchLocation(givenLocation);
    } else {
      goToCurrentLocation();
    }
  }, []);

  useEffect(() => {
    isAddressFromSearchRef.current = isAddressFromSearch;
  }, [isAddressFromSearch]);

  useEffect(() => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.permissions?.query ||
      editAddress ||
      isLocationV2Available()
    ) {
      return;
    }

    let permissionResult: PermissionStatus | null = null;

    const handlePermissionChange = () => {
      if (permissionResult?.state === 'granted') {
        goToCurrentLocation();
      }
    };

    navigator.permissions.query({ name: 'geolocation' }).then(result => {
      permissionResult = result;
      result.addEventListener('change', handlePermissionChange);
    });

    return () => {
      permissionResult?.removeEventListener('change', handlePermissionChange);
    };
  }, [editAddress, goToCurrentLocation]);

  useEffect(() => {
    if (address && +address?.longitude && +address?.latitude) {
      onSelectAddress(address);
    }
  }, [address]);

  useEffect(() => {
    if ([AddressStatusType.Denied, AddressStatusType.Failed].includes(locationAddress.status)) {
      isFetchingDeviceLocationRef.current = false;
      return;
    }

    if (!locationAddress?.data || isAddressFromSearchRef.current) {
      return;
    }

    const coords = {
      lat: locationAddress.data.latitude,
      lng: locationAddress.data.longitude,
    };

    setAddress(prev => ({
      ...prev,
      ...locationAddress.data,
      name: undefined,
      vicinity: undefined,
    }));

    setLocation(prev => {
      const shouldUseDeviceLocation = isFetchingDeviceLocationRef.current;
      const hasCurrentDeviceCoords =
        prev.activeLocType === 'device' && prev.deviceLoc?.status === LOCATION_STATUS_TYPES.Success;

      if (shouldUseDeviceLocation || hasCurrentDeviceCoords) {
        isFetchingDeviceLocationRef.current = false;

        return {
          ...prev,
          activeLocType: 'device',
          deviceLoc: {
            status: LOCATION_STATUS_TYPES.Success,
            coords,
          },
          searchLoc: {
            coords,
            placeId: 1,
            formattedAddress: locationAddress.data.locality,
          },
        };
      }

      return {
        ...prev,
        activeLocType: 'search',
        searchLoc: {
          coords,
          placeId: 1,
          formattedAddress: locationAddress.data.locality,
        },
      };
    });
  }, [locationAddress]);

  return (
    <>
      <Modal
        show
        head={{
          show: false,
          title: '',
          onClose: () => onClose(),
        }}
        variant="page"
        closeOnClickOutside={false}
        noPadding
        animation={{ type: 'btt', timeout: 's' }}
        rootStyle={{ zIndex }}
      >
        <div className={s.root}>
          <StatusBar statusBarColor="#FFFFFF" theme="dark" cleanUpOnUnmount />
          <div className={s.back_button} style={{ zIndex: 1 }} aria-hidden>
            <AppBar
              title={{ label: isEditAddress ? 'Edit address' : 'Add address' }}
              backButton={{ show: true, onClick: () => onClose() }}
              isTransparent
            />
            <div className={s.search_container}>
              <button
                type="button"
                className={s.searchBox}
                onClick={() => setShowAutoComplete(true)}
              >
                <Icon name="SearchRegular" color="secondary" width={20} height={20} />
                <span className={s.text}>
                  <Typography
                    label="Start typing your locality or area..."
                    color="placeholder"
                    variant="label_3_medium"
                  />
                </span>
              </button>
            </div>
          </div>

          <div className={s.map_parent}>
            {mapStatus === 'success' && <Map onDragEnd={onDragEnd} location={location} />}
          </div>

          {/* bottom fixed address */}
          <div className={s.bottom_parent}>
            <Typography label="Selected location" variant="label_3_medium" color="brand" />

            {!isAddressFromSearch &&
            [AddressStatusType.Default, AddressStatusType.Pending].includes(
              locationAddress.status,
            ) ? (
              <Shimmer />
            ) : (
              <div className={s.currentLocation}>
                <div className={s.loader}>
                  <div className={s.iconWrap}>
                    <div className={s.icon}>
                      <Icon name="LocationBold" />
                    </div>
                  </div>
                  <div className={s.text}>
                    {renderText(
                      isEditAddress,
                      address,
                      isAddressFromSearch
                        ? { status: AddressStatusType.Success, data: address }
                        : locationAddress,
                    )}
                  </div>
                </div>

                {(isAddressFromSearch ||
                  (![AddressStatusType.Denied, AddressStatusType.Failed].includes(
                    locationAddress.status,
                  ) &&
                    (locationAddress.data || isEditAddress))) &&
                  isServiceable && (
                    <div className={s.bottom_search_container}>
                      <div
                        className={s.searchBox}
                        onClick={() => {
                          setAddressPopup(true);
                          trackCustomGTMEvent({
                            eventName: 'pm_35_confirm_location',
                            attributes: {
                              category: EventCategoryName.Store,
                              device_id: getDeviceId(),
                            },
                          });
                        }}
                      >
                        <Button label="Confirm Location" fullWidth size="large" variant="primary" />
                      </div>
                    </div>
                  )}
              </div>
            )}

            <div
              className={cn('cmn_touchable_area', s.mapcenter)}
              onClick={e => {
                e.stopPropagation();
                goToCurrentLocation();
              }}
            >
              <Icon name="GPS" color="brand" width={30} height={30} />
            </div>
          </div>

          <GoogleMapScript />
        </div>
      </Modal>
      {showAutoComplete && mapStatus === 'success' && (
        <SelectLocation
          show={showAutoComplete && mapStatus === 'success'}
          onClose={() => setShowAutoComplete(false)}
          customRootStyle={{ zIndex }}
          onDone={(val: Address) => {
            setShowAutoComplete(false);
            isFetchingDeviceLocationRef.current = false;

            if (val.name) {
              setIsAddressFromSearch(true);
              isAddressFromSearchRef.current = true;
              setAddress(prev => ({ ...prev, ...val }));
              setLocation(prev => ({
                ...prev,
                activeLocType: 'search',
                searchLoc: {
                  coords: { lat: +val.latitude, lng: +val.longitude },
                  placeId: 1,
                  formattedAddress: val.locality,
                },
              }));
            } else {
              setIsAddressFromSearch(false);
              isAddressFromSearchRef.current = false;
              fetchLocation({ lat: +val.latitude, lng: +val.longitude });
            }
          }}
          locationV2RequestPayload={locObj}
          geoLocationFlow={INFRA_GEO_LOCATION_FLOW}
          addressValidation={addressValidation}
          serviceName={serviceName}
        />
      )}

      <Unserviceable
        isShow={showUnserviceable}
        onClose={() => setShowUnserviceable(false)}
        onDone={() => setShowUnserviceable(false)}
        zIndex={zIndex}
      />

      <AddAddress
        key={String(addressPopup)}
        isShow={addressPopup}
        fetchedAddress={address}
        isEditAddress={isEditAddress}
        customRootStyle={{ zIndex }}
        onClose={() => {
          setAddressPopup(false);
        }}
        onDone={() => {
          onDone();
        }}
      />
    </>
  );
}
